import React from "react";
import { Link } from "react-router-dom";
import SiteShell from "../components/layout/SiteShell";
import ContentPageLayout from "../components/content/ContentPageLayout";
import SEO from "../components/seo/SEO";
import { HOME_CTA_DARK_OUTLINE, HOME_CTA_DARK_PRIMARY } from "../lib/homeCta";
import { getMixInCards, MIX_IN_PAIRINGS } from "../lib/mixIns";

export default function MixInsPage() {
  const cards = getMixInCards();

  return (
    <SiteShell theme="dark">
      <SEO
        title="Sea Moss Mix-Ins | Ginger, Turmeric, Cinnamon & More | Moss Matrix"
        description="Explore herbs, seeds and powders people commonly pair with sea moss in smoothies, oats, juice, tea and simple daily routines. Food and routine inspiration only."
        path="/mix-ins"
      />
      <ContentPageLayout
        eyebrow="Mix-ins"
        title="Sea Moss Mix-Ins"
        subtitle="Explore herbs, seeds and powders people commonly pair with sea moss."
      >
        <p className="max-w-3xl text-base text-sage leading-relaxed m-0 mb-4">
          Explore herbs, seeds and powders people commonly pair with sea moss in smoothies, oats, juice, tea and simple daily
          routines.
        </p>
        <p className="max-w-3xl text-sm text-sage/80 leading-relaxed m-0 mb-8">
          This page is for food and routine inspiration only. Moss Matrix Sea Moss Gel is sold as a food product, not a medicine
          or performance supplement.
        </p>

        <div className="flex flex-wrap gap-3 mb-14">
          <Link to="/shop" className={`${HOME_CTA_DARK_PRIMARY} px-7 py-3.5 text-sm`}>
            Shop plain sea moss
          </Link>
          <Link to="/learn/gold-vs-purple-sea-moss" className={`${HOME_CTA_DARK_OUTLINE} px-7 py-3.5 text-sm`}>
            Gold or Purple?
          </Link>
        </div>

        <section aria-labelledby="featured-mixins-heading" className="mb-16">
          <h2 id="featured-mixins-heading" className="font-sans font-semibold text-3xl md:text-4xl text-ivory m-0 leading-tight mb-8">
            Featured mix-ins
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {cards.map((card) => (
              <Link
                key={card.slug}
                to={`/ingredients/${card.slug}`}
                className="group home-glass-card rounded-3xl overflow-hidden flex flex-col"
              >
                <div className="aspect-[16/10] bg-[#1f2c25] overflow-hidden">
                  <img
                    src={card.image}
                    alt={card.imageAlt}
                    className="w-full h-full object-cover editorial-img transition duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-sans font-semibold text-xl text-ivory m-0 leading-tight group-hover:text-goldhover transition">
                    {card.name}
                  </h3>
                  <p className="mt-2 text-sm text-sage leading-relaxed flex-1 m-0">{card.blurb}</p>
                  <span className="mt-4 text-xs tracking-widest uppercase text-sage/80">
                    {card.learnFirst ? "Learn more →" : "Commonly mixed with sea moss →"}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section aria-labelledby="pairings-heading" className="mb-16">
          <h2 id="pairings-heading" className="font-sans font-semibold text-3xl md:text-4xl text-ivory m-0 leading-tight mb-3">
            Simple mix-in ideas
          </h2>
          <p className="text-sm text-sage/80 mb-8 m-0">Inspiration only. These pairings are not added to your basket.</p>
          <div className="grid sm:grid-cols-2 gap-4 md:gap-5">
            {MIX_IN_PAIRINGS.map((pair) => (
              <article key={pair.title} className="home-glass-card rounded-3xl p-6">
                <h3 className="font-sans font-semibold text-xl text-ivory m-0 leading-tight">{pair.title}</h3>
                <p className="mt-2 text-sm text-sage leading-relaxed m-0">{pair.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section
          aria-labelledby="coming-later-heading"
          className="rounded-3xl border border-white/14 bg-panel/70 px-6 py-8 md:px-8"
        >
          <h2 id="coming-later-heading" className="font-sans font-semibold text-2xl md:text-3xl text-ivory m-0 leading-tight">
            Custom blends are being simplified
          </h2>
          <p className="mt-3 text-sm md:text-base text-sage leading-relaxed m-0">
            To keep orders fresh and manageable, Moss Matrix is focusing on plain Gold and Purple first. Selected custom
            mix-ins may return once the direct website is fully live.
          </p>
        </section>
      </ContentPageLayout>
    </SiteShell>
  );
}
